import React, { useState } from 'react';
import { EXCEL_FUNCTIONS, EXCEL_FUNCTION_CATEGORIES } from '../data/functionsData';
import functionsDetails from '../data/functionsDetails.json';

export default function FunctionDetail({ slug, navigate }) {
  const [copied, setCopied] = useState(false);

  // Look up the active function by slug
  const funcItem = EXCEL_FUNCTIONS.find((f) => f.slug === slug);
  if (!funcItem) {
    return (
      <main className="container" style={{ padding: '80px 24px', textAlign: 'center' }}>
        <h2>Function not found</h2>
        <p>The requested Excel function could not be found.</p>
        <button className="btn btn-primary" onClick={() => navigate('functions')}>
          Back to Functions
        </button>
      </main>
    );
  }

  // Get matching details
  const details = functionsDetails[slug] || { gridHtml: '', formulaHtml: '', exampleHtml: '' };
  
  // Find category icon
  const categoryMeta = EXCEL_FUNCTION_CATEGORIES.find((c) => c.id === funcItem.category) || {};

  // Find related functions
  const relatedItems = (funcItem.related || [])
    .map((rSlug) => EXCEL_FUNCTIONS.find((f) => f.slug === rSlug))
    .filter(Boolean);

  const handleCopyFormula = () => {
    navigator.clipboard.writeText(funcItem.example);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCategoryJump = (e) => {
    e.preventDefault();
    navigate('functions');
    // Allow navigation to complete before scrolling
    setTimeout(() => {
      document.getElementById(funcItem.category)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  return (
    <main className="function-detail" style={{ marginTop: '20px' }}>
      <div className="container">
        <nav className="breadcrumbs" aria-label="Breadcrumb">
          <a href="#/functions" onClick={(e) => { e.preventDefault(); navigate('functions'); }}>
            Excel Functions
          </a>
          <span>›</span>
          <a href="#/functions" onClick={handleCategoryJump}>
            {funcItem.categoryLabel}
          </a>
          <span>›</span>
          <span>{funcItem.name}</span>
        </nav>

        <div className="function-detail-grid">
          <article className="function-detail-card">
            <div className="function-meta-row">
              <span className="tag">{funcItem.categoryLabel}</span>
              <span className="tag">{funcItem.usageLabel}</span>
            </div>

            <h1>={funcItem.name}</h1>
            <p className="lead">{funcItem.description}</p>

            {/* Formula Demo Area */}
            {(details.formulaHtml || details.gridHtml) && (
              <section className="excel-demo-panel">
                <div className="excel-demo-header">
                  <span className="demo-dot"></span>
                  <span className="demo-dot"></span>
                  <span className="demo-dot"></span>
                  <span className="demo-title">Excel Worksheet Example</span>
                </div>
                
                {details.formulaHtml && (
                  <div 
                    className="excel-demo-formula" 
                    dangerouslySetInnerHTML={{ __html: details.formulaHtml }} 
                  />
                )}

                {details.gridHtml && (
                  <div className="excel-grid-container" style={{ overflowX: 'auto' }}>
                    <div 
                      className="excel-grid" 
                      dangerouslySetInnerHTML={{ __html: details.gridHtml }} 
                    />
                  </div>
                )}
              </section>
            )}

            {/* What it does */}
            <section className="detail-block">
              <h2>What this function does</h2>
              <p>{funcItem.description}</p>
              {funcItem.note && (
                <div className="detail-callout" style={{ marginTop: '14px', borderLeft: '3px solid #146c43', paddingLeft: '12px' }}>
                  <strong>Note: </strong>{funcItem.note}
                </div>
              )}
            </section>

            {/* How to use it */}
            <section className="detail-block">
              <h2>How to use it</h2>
              <p>Enter the formula using the following arguments:</p>
              <pre className="syntax-block">
                <code>{funcItem.syntax}</code>
              </pre>
            </section>

            {/* Practical Excel example */}
            {details.exampleHtml ? (
              <section className="detail-block">
                <h2>Practical Excel example</h2>
                <div 
                  className="practical-example" 
                  dangerouslySetInnerHTML={{ __html: details.exampleHtml }} 
                />
              </section>
            ) : (
              <section className="detail-block">
                <h2>Practical Excel example</h2>
                <div className="practical-example">
                  <p><strong>Example formula:</strong></p>
                  <div className="example-formula-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f5f7f6', padding: '10px 14px', borderRadius: '6px', margin: '10px 0' }}>
                    <code>{funcItem.example}</code>
                    <button 
                      className="btn btn-secondary btn-sm" 
                      onClick={handleCopyFormula}
                      style={{ fontSize: '0.8rem', padding: '4px 8px' }}
                    >
                      {copied ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <p>This returns the value: <strong>{funcItem.result}</strong></p>
                </div>
              </section>
            )}
          </article>

          <aside className="detail-aside">
            <div className="detail-aside-card">
              <h3>Category</h3>
              <p className="muted">{categoryMeta.description || funcItem.categoryDescription}</p>
              <a 
                className="btn btn-secondary btn-sm back-functions" 
                href="#/functions"
                onClick={handleCategoryJump}
              >
                View Category
              </a>
            </div>

            {relatedItems.length > 0 && (
              <div className="detail-aside-card">
                <h3>Related functions</h3>
                <div className="related-list">
                  {relatedItems.map((rel) => (
                    <a
                      key={rel.slug}
                      className="related-link"
                      href={`#/functions/${rel.slug}`}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(`functions/${rel.slug}`);
                        window.scrollTo({ top: 0 });
                      }}
                    >
                      ={rel.name}
                    </a>
                  ))}
                </div>
              </div>
            )}

            <div className="detail-aside-card">
              <h3>Version compatibility</h3>
              <p className="shortcut-note">Compatible with: <strong>{funcItem.version}</strong></p>
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
}
